import { OrbitControls } from '@react-three/drei'
import { useThree, useFrame } from '@react-three/fiber'
import { useRef, useState, Suspense, useEffect, lazy } from 'react'
import { EffectComposer, ToneMapping, Bloom } from '@react-three/postprocessing'
import { ToneMappingMode } from 'postprocessing'
import * as THREE from 'three'
import { useGLTF } from '@react-three/drei'
import useGame from './stores/useGame.js'

// ESSENTIAL components (loaded before Enter)
import HuoneBaked from './HuoneBaked.js'
import Kirjat from './Kirjat.js'
import Infoboard from './Infoboard.js'
import MusicBG from './MusicBG.js'
import MusicScene from './MusicScene.js'

// HEAVY / DEFERRED components (lazy)
const KatuBaked = lazy(() => import('./KatuBaked.js'))
const InstallaatioBaked = lazy(() => import('./InstallaatioBaked.js'))
const Installaatio = lazy(() => import('./Installaatio.js'))
const Posters = lazy(() => import('./Posters.js'))
const Stage = lazy(() => import('./Stage.js'))

export default function Experience({ entered = false }) {
  const space = useGame((state) => state.space)
  const { camera } = useThree()
  const controlsRef = useRef()
  const [targetPosition, setTargetPosition] = useState(null)
  const [targetLookAt, setTargetLookAt] = useState(null)
  const transitionDone = useGame((state) => state.transitionDone)
  const setTransitionDone = useGame((state) => state.setTransitionDone)
  const [playSceneSound, setPlaySceneSound] = useState(false)

  // ------------------------
  // CAMERA POSITIONS
  // ------------------------
  const reset = () => {
    const target = new THREE.Vector3(-2, 3.6, -5.5) // wall
    const newCamPos = new THREE.Vector3(-2, 3.6, -3.6)
    setTargetPosition(newCamPos)
    setTargetLookAt(target)
  }
  const reset2 = () => {
    const target = new THREE.Vector3(-0.8, -0.3, 1.1) // table
    const newCamPos = new THREE.Vector3(0.3, 1, 2.7)
    setTargetPosition(newCamPos)
    setTargetLookAt(target)
  }
  const reset3 = () => {
    const target = new THREE.Vector3(97.2, 1, -1) // alley
    const newCamPos = new THREE.Vector3(103, 0, 4)
    setTargetPosition(newCamPos)
    setTargetLookAt(target)
  }
  const reset4 = () => {
    const target = new THREE.Vector3(117.5, 6.5, 10.5) // arthouse wall
    const newCamPos = new THREE.Vector3(90, -5, 39.5)
    setTargetPosition(newCamPos)
    setTargetLookAt(target)
  }
  const reset5 = () => {
    const target = new THREE.Vector3(117.5, 2, 14.5) // stage
    const newCamPos = new THREE.Vector3(155, -2, 15)
    setTargetPosition(newCamPos)
    setTargetLookAt(target)
  }

  // ------------------------
  // INITIAL CAMERA TARGET
  // ------------------------
  useEffect(() => {
    const initialTarget = new THREE.Vector3(4, 1.1, 0)
    const initialCamPos = new THREE.Vector3(6, 1.1, 0)
    setTargetLookAt(initialTarget)
    setTargetPosition(initialCamPos)
    if (controlsRef.current) {
      controlsRef.current.target.copy(initialTarget)
      controlsRef.current.update()
    }
  }, [])

  // ------------------------
  // SPACE TRANSITION HANDLER
  // ------------------------
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.target.set(0, 2, -0.1)
      controlsRef.current.update()
    }

    return useGame.subscribe(
      (state) => state.space,
      (value) => {
        setTransitionDone(false)
        setPlaySceneSound(false)

        if (value === 'wall') reset()
        if (value === 'table') reset2()
        if (value === 'alley') reset3()
        if (value === 'arthouse') reset4()
        if (value === 'stage') reset5()
      }
    )
  }, [])

// ------------------------
// CAMERA LERP (FPS-independent, cubic easing)
// ------------------------
// Adjusted camera lerp — device-independent
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 1.5)
const baseLerpSpeed = 0.03 // you can tweak this to taste

let lastTime = performance.now()

useFrame(() => {
  if (
    targetPosition &&
    targetLookAt &&
    controlsRef.current &&
    Number.isFinite(targetPosition.x)
  ) {
    const now = performance.now()
    const delta = (now - lastTime) / 16.6667 // normalize to ~60fps
    lastTime = now

    const lerpFactor = 1 - Math.pow(1 - baseLerpSpeed, delta)
    const eased = easeOutCubic(lerpFactor)

    camera.position.lerp(targetPosition, eased)
    controlsRef.current.target.lerp(targetLookAt, eased)
    controlsRef.current.update()

    const targetClose =
      controlsRef.current.target.distanceTo(targetLookAt) < 1.3

    if (targetClose && !transitionDone) {
      setTransitionDone(true)
      setPlaySceneSound(true)
    }
  }
})



  // ------------------------
  // DEFERRED ASSET PRELOAD (only after entered)
  // ------------------------
  useEffect(() => {
    if (entered) {
      useGLTF.preload('/Katu_posterit_baked-draco.glb')
      useGLTF.preload('/Installaatio-draco.glb')
      useGLTF.preload('/Katu_installaatio_baked-draco.glb')
      useGLTF.preload('/Posters.glb')
      useGLTF.preload('/Stage_baked-draco.glb')
    }
  }, [entered])

  // ------------------------
  // RENDER
  // ------------------------
  return (
    <>
      <EffectComposer>
        <Bloom 
          luminanceThreshold={2} // include all light, not just very bright
          luminanceSmoothing={0.9} // soft transition between bloom/no bloom
          intensity={1.2}
          mipmapBlur />
        <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
      </EffectComposer>

      <OrbitControls
        ref={controlsRef}
        makeDefault
        enableDamping
        dampingFactor={0.1}
        zoomSpeed={0.1}
        rotateSpeed={-0.15}
        minDistance={1}
        maxDistance={10}
      />

      <ambientLight intensity={1.5} />
      <MusicBG />
      <MusicScene active={playSceneSound} />

      {/* === ESSENTIAL SCENE === */}
      <Suspense fallback={null}>
        <Kirjat scale={0.25} position-y={2.31} current={space} />
        <Infoboard
          scale={0.078}
          position={[-1.9, 3.35, -5.5]}
          current={space}
        />
        <HuoneBaked scale={0.25} position-y={2.31} current={space} />
      </Suspense>

      {/* === DEFERRED SCENE (only after enter) === */}
      {entered && (
        <Suspense fallback={null}>
          <KatuBaked scale={0.1} position={[100, -1, 0]} current={space} />
          <InstallaatioBaked scale={0.1} position={[100, -1, 0]} current={space} />
          <Installaatio scale={0.1} position={[100, -1, 0]} current={space} />
          <Posters scale={0.1} position={[100, -1, 0]} current={space} />
          <Stage scale={1} position={[117.5, 2, 14.5]} current={space} />
        </Suspense>
      )}
    </>
  )
}
